import { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import {
  AppCheckBox,
  AppFormikTextField,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import { ReactComponent as MiniDocument } from 'assets/images/ic_mini-document.svg';
import { useFormikContext } from 'formik';
import ReactQuill from 'react-quill-new';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import 'react-quill-new/dist/quill.snow.css';
import Frameworks from './Templates.json';

const PolicyContentCard = (): ReactElement => {
  const [showTemplate, setShowTemplate] = useState(false);
  const { values, setFieldValue }: any = useFormikContext();
  const [templates, setTemplates] = useState(Frameworks[0].templates);
  const [selectedFrameworkId, setSelectedFrameworkId] = useState(
    Frameworks[0].id
  );
  const [templateIds, setTemplateIds] = useState<string[]>([]);

  // Instance Methods
  const resetTemplates = (): void => {
    setTemplateIds([]);
    setSelectedFrameworkId(Frameworks[0].id);
    setTemplates(Frameworks[0].templates);
  };

  // UI Actions
  const actionInsertSelectedTemplates = (): void => {
    let newContent = values.content;
    templateIds.forEach((t, index) => {
      const template = templates.find((tt) => tt.id === t);
      if (template) {
        newContent = `${newContent}<br/><p><b>${index + 1}.${
          template.title
        }</b></p><p>${template.content}</p><br/><p>Covers: ${
          template.covers
        }</p><br/>`;
      }
    });
    setFieldValue('content', newContent, true);
    setShowTemplate(false);
    resetTemplates();
  };

  useEffect(() => {
    resetTemplates();
  }, []);

  // UI Elements
  return (
    <Paper variant="outlined" className="policy-card">
      <Typography variant="h6">Policy Content</Typography>
      <Typography className="mb-4">
        {values?.policyMode === 'Url'
          ? 'Provide the URL to your existing policy document '
          : 'The main body of the policy document'}
      </Typography>
      {values?.policyMode === 'Content' && (
        <Stack direction="row" spacing={2} alignItems="center" className="mb-4">
          <Typography variant="body2">Content</Typography>
          <AppPrimaryButton
            onClick={() => setShowTemplate(true)}
            startIcon={
              <SvgIcon
                component={MiniDocument}
                inheritViewBox
                className="view-icon"
                fontSize="inherit"
                htmlColor="#3D3C42"
              />
            }
            title="Add Template Language"
          />
        </Stack>
      )}
      {values?.policyMode === 'Url' && (
        <AppFormikTextField
          name="policyUrl"
          placeholder="https://example.com/policy-document.pdf"
          label="Policy URL"
          outerLabel
        />
      )}
      {values?.policyMode === 'Content' && (
        <ReactQuill
          theme="snow"
          value={values.content}
          onChange={(value) => {
            setFieldValue('content', value, true);
          }}
        />
      )}
      <Dialog maxWidth="md" fullWidth open={showTemplate}>
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Stack>
              <Typography variant="h3">Insert Template Language</Typography>
              <Typography variant="body1">
                Select sections from policy frameworks to insert into your
                policy content.
              </Typography>
            </Stack>
            <IconButton
              onClick={() => {
                setShowTemplate(false);
                resetTemplates();
              }}
            >
              <HighlightOffIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={4}>
            <Grid item xs={4}>
              <Typography variant="h3">Frameworks</Typography>
              <Box
                style={{
                  border: '1px solid lightgray',
                  borderRadius: '10px',
                  padding: 10,
                  marginTop: 16,
                  maxHeight: '400px',
                  height: '400px',
                  overflowY: 'auto',
                }}
              >
                {Frameworks.map((framework) => (
                  <Box
                    style={{
                      border:
                        selectedFrameworkId === framework.id
                          ? '2px solid black'
                          : '1px solid lightgray',
                      borderRadius: '10px',
                      padding: 10,
                      marginTop: 16,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setTemplates(framework.templates);
                      setSelectedFrameworkId(framework.id);
                      setTemplateIds([]);
                    }}
                  >
                    <Typography className="fw-6">{framework.name}</Typography>
                    <Typography variant="body2">
                      {framework.description}
                    </Typography>
                    <Chip size="small" label={framework.version} />
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="h3">Template Sections</Typography>
              <Box
                style={{
                  border: '1px solid lightgray',
                  borderRadius: '10px',
                  padding: 10,
                  marginTop: 16,
                  maxHeight: '400px',
                  height: '400px',
                  overflowY: 'auto',
                }}
              >
                {templates.map((template, index) => (
                  <Stack
                    direction="row"
                    style={{
                      border: templateIds.includes(template.id)
                        ? '1px solid black'
                        : '1px solid lightgray',
                      borderRadius: '10px',
                      padding: 10,
                      marginTop: 16,
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                    onClick={() => {
                      const index = templateIds.findIndex(
                        (t) => t === template.id
                      );
                      if (index > -1) {
                        templateIds.splice(index, 1);
                      } else {
                        templateIds.push(template.id);
                      }
                      setTemplateIds([...templateIds]);
                    }}
                  >
                    <AppCheckBox checked={templateIds.includes(template.id)} />
                    <Box className="policy-template">
                      <Typography className="fw-6">
                        {index + 1}.{template.title}
                      </Typography>
                      <Chip size="small" label={template.covers} />
                      <Typography variant="body2" className="">
                        {template.content}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 5 }}>
          <Typography style={{ color: 'gray' }}>
            {templateIds.length} sections selected
          </Typography>
          <AppPrimaryButton
            onClick={actionInsertSelectedTemplates}
            disabled={templateIds.length === 0}
            className="btn-small"
            title={`Insert selected ${templateIds.length}`}
          />
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PolicyContentCard;
